import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Minus, Trash2, ShoppingBasket, Loader2, CheckCircle2, Lock } from "lucide-react";
import { api } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import BankDetails from "../../components/BankDetails";
import { DELIVERY_ZONES, gbp, formatApiErrorDetail } from "../../lib/constants";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Dialog, DialogContent } from "../../components/ui/dialog";

export default function Cart() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    items, updateQuantity, removeItem, clearCart, subtotal, fixedCharge,
    deliveryCharge, total, zone, setDeliveryZone,
  } = useCart();

  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    delivery_name: "", delivery_phone: "", delivery_address: "", delivery_postcode: "",
    notes: "",
  });
  const [error, setError] = useState("");
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);

  useEffect(() => {
    api.get("/customer/market-day/status").then((r) => setStatus(r.data));
    if (user) {
      setForm((f) => ({ ...f, delivery_name: user.name || "", delivery_phone: user.phone || "" }));
      if (user.delivery_zone) setDeliveryZone(user.delivery_zone);
    }
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const open = status?.is_open;

  const placeOrder = async () => {
    setError("");
    if (!open) {
      setError("Orders are currently closed. Please check back on the next Market Day.");
      return;
    }
    if (!form.delivery_name || !form.delivery_phone || !form.delivery_address || !form.delivery_postcode) {
      setError("Please complete all delivery details.");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await api.post("/customer/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        delivery_name: form.delivery_name,
        delivery_phone: form.delivery_phone,
        delivery_address: form.delivery_address,
        delivery_postcode: form.delivery_postcode,
        delivery_zone: zone,
        notes: form.notes,
      });
      setPlaced(data);
      clearCart();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not place order.");
    } finally {
      setPlacing(false);
    }
  };

  if (items.length === 0 && !placed) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-10">
        <div data-testid="empty-cart" className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-[#E8F5E9] flex items-center justify-center mb-5">
            <ShoppingBasket size={34} className="text-[#228B22]" />
          </div>
          <h2 className="font-display text-2xl font-bold text-[#0A1F13]">Your basket is empty</h2>
          <p className="text-gray-500 mt-2">Add some fresh groceries to get started.</p>
          <button
            data-testid="browse-products-button"
            onClick={() => navigate("/home")}
            className="mt-6 px-6 h-12 rounded-full bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold transition-colors duration-200"
          >
            Browse groceries
          </button>
        </div>
      </div>
    );
  }

  const field = "w-full h-12 px-4 rounded-xl bg-gray-100 focus:bg-white border border-transparent focus:border-[#228B22] outline-none transition-colors";

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 pt-6">
      <h1 className="font-display text-3xl font-bold text-[#0A1F13] tracking-tight mb-6">Your Basket</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6 min-w-0">
          {/* Items */}
          <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
            {items.map((i) => (
              <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex items-center gap-3 p-4">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#0A1F13] truncate">{i.product_name}</p>
                  <p className="text-sm text-gray-500">{gbp(i.price)} / {i.unit}</p>
                </div>
                <div className="flex items-center gap-1 bg-[#E8F5E9] rounded-full px-1 h-9">
                  <button data-testid={`cart-decrease-${i.product_id}`} onClick={() => updateQuantity(i.product_id, i.quantity - 1)} className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-[#1A4331]"><Minus size={14} /></button>
                  <span className="w-6 text-center font-semibold text-[#1A4331]">{i.quantity}</span>
                  <button data-testid={`cart-increase-${i.product_id}`} onClick={() => updateQuantity(i.product_id, i.quantity + 1)} className="w-7 h-7 rounded-full bg-[#228B22] text-white flex items-center justify-center"><Plus size={14} /></button>
                </div>
                <div className="w-16 text-right font-semibold text-[#0A1F13]">{gbp(i.price * i.quantity)}</div>
                <button data-testid={`cart-remove-${i.product_id}`} onClick={() => removeItem(i.product_id)} className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={18} /></button>
              </div>
            ))}
          </div>

          {/* Delivery details */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h2 className="font-semibold text-lg text-[#0A1F13] mb-4">Delivery Details</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="delivery-name-input" placeholder="Full name" value={form.delivery_name} onChange={set("delivery_name")} className={field} />
              <input data-testid="delivery-phone-input" placeholder="Phone" value={form.delivery_phone} onChange={set("delivery_phone")} className={field} />
              <input data-testid="delivery-address-input" placeholder="Address" value={form.delivery_address} onChange={set("delivery_address")} className={field + " sm:col-span-2"} />
              <input data-testid="delivery-postcode-input" placeholder="Postcode" value={form.delivery_postcode} onChange={set("delivery_postcode")} className={field} />
              <Select value={zone} onValueChange={setDeliveryZone}>
                <SelectTrigger data-testid="delivery-zone-select" className="h-12 rounded-xl bg-gray-100 border-transparent"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DELIVERY_ZONES.map((z) => (
                    <SelectItem key={z.name} value={z.name}>{z.name} {z.charge > 0 ? `(+£${z.charge})` : "(Free)"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <textarea data-testid="delivery-notes-input" placeholder="Order notes (optional)" value={form.notes} onChange={set("notes")} className="w-full min-h-[80px] p-4 rounded-xl bg-gray-100 focus:bg-white border border-transparent focus:border-[#228B22] outline-none transition-colors sm:col-span-2" />
            </div>
          </div>

          <BankDetails reference={user?.name} />
        </div>

        {/* Summary */}
        <div className="lg:col-span-1 min-w-0">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 lg:sticky lg:top-24">
            <h2 className="font-semibold text-lg text-[#0A1F13] mb-4">Order Summary</h2>
            <div className="space-y-2.5 text-sm">
              <Row label="Subtotal" value={gbp(subtotal)} testid="summary-subtotal" />
              <Row label={subtotal > 100 ? "Service Charge (12%)" : "Fixed Charge"} value={gbp(fixedCharge)} testid="summary-fixed" />
              <Row label={`Delivery (${zone})`} value={gbp(deliveryCharge)} testid="summary-delivery" />
              <div className="border-t border-gray-100 pt-3 mt-3 flex justify-between text-base font-bold text-[#0A1F13]">
                <span>Total</span>
                <span data-testid="summary-total">{gbp(total)}</span>
              </div>
            </div>

            <p data-testid="service-charge-note" className="mt-3 text-xs text-gray-400 leading-relaxed">
              {subtotal > 100
                ? "A 12% service charge applies to orders over £100."
                : "Orders up to £100 carry a £10 fixed charge. Orders over £100 are charged 12% of the order value instead."}
              {zone !== "Dorchester" && " Deliveries outside Dorchester include a £10 token."}
            </p>

            {error && (
              <div data-testid="cart-error" className="mt-4 flex items-start gap-2 rounded-xl bg-[#FFF0F0] border border-[#FFD0D0] px-3 py-2.5 text-sm text-[#991B1B]">
                {error}
              </div>
            )}

            <button
              data-testid="place-order-button"
              onClick={placeOrder}
              disabled={placing || !open}
              className="w-full h-[52px] mt-5 rounded-full bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold flex items-center justify-center gap-2 transition-colors duration-200 disabled:bg-gray-200 disabled:text-gray-400"
            >
              {placing ? <Loader2 size={20} className="animate-spin" /> : !open ? (<><Lock size={16} /> Orders Closed</>) : "Place Order"}
            </button>
            {!open && <p className="text-xs text-gray-400 text-center mt-2">{status?.message}</p>}
          </div>
        </div>
      </div>

      {/* Success modal */}
      <Dialog open={!!placed} onOpenChange={(o) => { if (!o) { setPlaced(null); navigate("/orders"); } }}>
        <DialogContent data-testid="order-success-modal" className="max-w-md rounded-2xl">
          {placed && (
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-[#DCFCE7] flex items-center justify-center mb-4">
                <CheckCircle2 size={36} className="text-[#166534]" />
              </div>
              <h2 className="font-display text-2xl font-bold text-[#0A1F13]">Order Placed!</h2>
              <p className="text-gray-500 mt-1">Reference #{placed.id.slice(-6).toUpperCase()}</p>
              <p className="text-xl font-bold text-[#1A4331] mt-3">{gbp(placed.total_amount)}</p>
              <div className="mt-5 text-left">
                <BankDetails reference={`#${placed.id.slice(-6).toUpperCase()}`} />
              </div>
              <div className="flex gap-3 mt-5">
                <button data-testid="view-orders-button" onClick={() => navigate("/orders")} className="flex-1 h-12 rounded-full bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold transition-colors duration-200">View Orders</button>
                <button data-testid="continue-shopping-button" onClick={() => { setPlaced(null); navigate("/home"); }} className="flex-1 h-12 rounded-full border border-[#228B22] text-[#228B22] font-semibold hover:bg-[#E8F5E9] transition-colors duration-200">Keep Shopping</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between text-gray-600">
      <span>{label}</span>
      <span data-testid={testid} className="font-medium text-[#0A1F13]">{value}</span>
    </div>
  );
}
